import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';

const TERMS_LAST_UPDATED = '12 November 2025';

const termsContent = {
  en: {
    title: 'Terms of Service',
    backLabel: 'Back to Home',
    languageNote:
      'These Terms are provided in English and Spanish. In case of conflict between translations, the Spanish version prevails.',
    tableOfContentsTitle: 'Table of Contents',
    sections: [
      {
        id: 'introduction',
        title: '1. Introduction',
        paragraphs: [
          'These Terms of Service ("Terms") govern access to and use of the Service by agencies, their authorised users, and invited clients (collectively, "Users"). By creating an account, accessing, or using the Service you accept these Terms, our Privacy Policy, and the Data Processing Agreement ("DPA"), which form an integral part of the contractual relationship. The DPA is available at [livelovespain.com/crm/dpa](/crm/dpa).',
          'If you are accepting these Terms on behalf of an organisation, you represent that you have authority to bind that organisation. References to “you” or “Agency” include the legal entity on whose behalf the Service is used.'
        ]
      },
      {
        id: 'definitions',
        title: '2. Definitions',
        list: [
          '“Agency” – a business customer subscribing to the Service.',
          '“User” – any individual authorised by an Agency to access the Service (including administrators, collaborators, or contractors).',
          '“Client” – an end-customer whose data is processed within the Service by an Agency.',
          '“Account” or “Seat” – an individual user profile linked to an Agency subscription.',
          '“Service Data” – all data, files, documents, forms, and content submitted or generated within the Service.',
          '“Personal Data” – any information relating to an identified or identifiable natural person as defined in GDPR.',
          '“Confidential Information” – all non-public information disclosed by either party in connection with the Service.'
        ]
      },
      {
        id: 'eligibility',
        title: '3. Eligibility and Account Registration',
        paragraphs: [
          'Users must be at least sixteen (16) years old and legally capable of entering into binding agreements under applicable law.',
          'Agencies are responsible for providing accurate registration details and for ensuring that user permissions reflect the organisational structure and data-access needs.',
          'LiveLoveSpain may suspend or terminate accounts where inaccurate information is supplied, identity cannot be verified, or the Service is used in breach of these Terms.'
        ]
      },
      {
        id: 'access',
        title: '4. Access and Use of the Service',
        paragraphs: [
          'LiveLoveSpain grants Agencies a non-exclusive, non-transferable, revocable licence to access and use the Service in accordance with these Terms and applicable law.',
          'Users shall not (a) copy, modify, or create derivative works of the Service; (b) reverse engineer or attempt to extract source code; (c) interfere with or disrupt the integrity or performance of the Service; (d) bypass security controls; (e) use the Service for unlawful, harmful, or fraudulent activity.',
          'LiveLoveSpain aims to provide 99.5% monthly availability but reserves the right to carry out maintenance, updates, or emergency downtime. Planned maintenance will be communicated in advance when reasonably possible.'
        ]
      },
      {
        id: 'billing',
        title: '5. Subscriptions, Billing, and Payment',
        paragraphs: [
          'Agency subscriptions are billed monthly or annually in advance via our payment processor, Stripe Payments Europe Limited. Charges include applicable VAT under EU law unless expressly stated.',
          'Subscriptions renew automatically at the end of each billing cycle unless cancelled in accordance with Section 6. Agencies can review billing information and invoices through the Service.',
          'You authorise LiveLoveSpain to charge the payment method on file for subscription fees, overage charges, and applicable taxes. Failed payments may result in suspended access.',
          'LiveLoveSpain may revise pricing with at least thirty (30) days’ prior written notice. Continued use of the Service after the effective date of the change constitutes acceptance.'
        ]
      },
      {
        id: 'trial',
        title: '6. Trials, Downgrades, and Cancellation',
        paragraphs: [
          'New Agencies may benefit from a fourteen (14) day free trial. Unless cancelled before the end of the trial, the subscribed plan will convert automatically to a paid subscription, with unused trial days applied as agreed.',
          'Agencies may cancel at any time from the billing portal. Cancellation becomes effective at the end of the current billing cycle; no refunds are provided for partial periods except where required by law.',
          'Upon termination, access to the Service will cease. Agencies are encouraged to export their data before the effective date of termination. LiveLoveSpain retains Service Data for up to thirty (30) days after termination to allow limited recovery, after which it is securely deleted unless retention is required by law.'
        ]
      },
      {
        id: 'ip',
        title: '7. Intellectual Property and Content Ownership',
        paragraphs: [
          'LiveLoveSpain retains all intellectual property rights in the Service, including software, interfaces, documentation, and branding.',
          'Agencies retain ownership of Service Data they upload or generate. By using the Service, Agencies grant LiveLoveSpain a limited licence to host, process, transmit, and display Service Data solely for the purpose of providing and improving the Service.',
          'Agencies are responsible for ensuring that Service Data does not infringe third-party rights and complies with applicable law.'
        ]
      },
      {
        id: 'data-protection',
        title: '8. Data Protection and GDPR Compliance',
        paragraphs: [
          'LiveLoveSpain acts as Data Processor for the personal data that Agencies manage within the Service. Agencies act as Data Controllers with respect to their clients. LiveLoveSpain acts as Data Controller for account and billing data relating to the Agency’s relationship with us.',
          'Processing is carried out in accordance with Regulation (EU) 2016/679 (GDPR), Ley Orgánica 3/2018 (LOPDGDD), and Ley 34/2002 (LSSI-CE).',
          'The Data Processing Agreement (DPA) governs processor obligations, subprocessors, security measures, and assistance with data subject rights. The DPA is incorporated by reference and available at [livelovespain.com/crm/dpa](/crm/dpa).',
          'LiveLoveSpain will notify the Agency without undue delay and within seventy-two (72) hours of becoming aware of a personal data breach impacting Service Data.'
        ]
      },
      {
        id: 'security',
        title: '9. Security and Confidentiality',
        paragraphs: [
          'LiveLoveSpain implements appropriate technical and organisational measures, including encryption in transit and at rest, role-based access controls, logging, regular backups, and vulnerability management.',
          'Each party agrees to treat confidential information with at least the same degree of care it uses to protect its own confidential information and to use such information solely for fulfilling contractual obligations.',
          'Agencies must ensure that users keep credentials confidential and promptly notify LiveLoveSpain of any suspected breach or unauthorised access.'
        ]
      },
      {
        id: 'third-parties',
        title: '10. Third-Party Services and Integrations',
        paragraphs: [
          'The Service may rely on third-party providers (for example, hosting, payment processing, email delivery, AI assistance). LiveLoveSpain selects these subprocessors with due diligence and maintains Data Processing Agreements with each.',
          'Use of optional third-party integrations provided by the Agency is at the Agency’s own risk and subject to the third party’s terms.'
        ]
      },
      {
        id: 'liability',
        title: '11. Limitation of Liability',
        paragraphs: [
          'To the maximum extent permitted by law, LiveLoveSpain is not liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of profits, revenue, data, or business opportunities.',
          'LiveLoveSpain’s aggregate liability arising out of or relating to these Terms shall not exceed the total fees paid by the Agency to LiveLoveSpain in the twelve (12) months preceding the event giving rise to liability.',
          'Nothing in these Terms limits liability for death or personal injury caused by negligence, for wilful misconduct, or for any other liability that cannot be excluded under applicable law.'
        ]
      },
      {
        id: 'termination',
        title: '12. Suspension and Termination',
        paragraphs: [
          'LiveLoveSpain may suspend or terminate access if: (a) invoices remain unpaid; (b) the Service is used in violation of law or these Terms; (c) there is a security threat to the Service; or (d) LiveLoveSpain is legally compelled to do so.',
          'Upon termination for cause, LiveLoveSpain may immediately disable access. Agencies remain responsible for all fees incurred up to the effective termination date.',
          'Agencies may request a copy of Service Data during the retention period described in Section 6. LiveLoveSpain has no obligation to retain Service Data beyond statutory requirements.'
        ]
      },
      {
        id: 'law',
        title: '13. Governing Law, Jurisdiction, and Dispute Resolution',
        paragraphs: [
          'These Terms are governed by the laws of Spain and applicable European Union regulations, without regard to conflict-of-law principles.',
          'The parties submit to the exclusive jurisdiction of the Courts of Madrid, Spain. Consumers within the EU may also access the European Commission’s Online Dispute Resolution platform (http://ec.europa.eu/odr).'
        ]
      },
      {
        id: 'modifications',
        title: '14. Changes to the Terms',
        paragraphs: [
          'LiveLoveSpain may amend these Terms to reflect legal, operational, or security requirements. Material changes will be communicated at least thirty (30) days in advance via email or in-app notice.',
          'Continued use of the Service after the effective date constitutes acceptance of the revised Terms. Previous versions will be archived for reference.'
        ]
      },
      {
        id: 'contact',
        title: '15. Contact',
        paragraphs: [
          'For questions about these Terms, please contact:',
          'LiveLoveSpain.',
          'Email: info@livelovespain.com'
        ]
      }
    ],
    spanishLinkLabel: 'Ver versión en español'
  },
  es: {
    title: 'Condiciones de Servicio',
    backLabel: 'Volver al inicio',
    languageNote:
      'Estas Condiciones se facilitan en español e inglés. En caso de discrepancia entre ambas versiones, prevalece la versión española.',
    tableOfContentsTitle: 'Índice',
    sections: [
      {
        id: 'introduccion',
        title: '1. Introducción',
        paragraphs: [
          'Las presentes Condiciones de Servicio ("Condiciones") regulan el acceso y uso de LiveLoveSpain CRM (el Servicio) por parte de las agencias, sus usuarios autorizados y los clientes invitados (en conjunto, los "Usuarios"). Al crear una cuenta, acceder o utilizar el Servicio usted acepta estas Condiciones, nuestra Política de Privacidad y el Acuerdo de Encargado de Tratamiento ("DPA"), que forman parte integrante del contrato. El DPA está disponible en [livelovespain.com/crm/dpa](/crm/dpa).',
          'Si acepta estas Condiciones en nombre de una entidad, declara y garantiza que dispone de la autoridad suficiente para vincularla. Las referencias a “usted” o “Agencia” incluyen a la entidad titular de la suscripción.'
        ]
      },
      {
        id: 'definiciones',
        title: '2. Definiciones',
        list: [
          '“Agencia” – cliente empresarial que contrata el Servicio.',
          '“Usuario” – persona autorizada por la Agencia para acceder al Servicio (incluye administradores, colaboradores o proveedores).',
          '“Cliente” – destinatario final cuyos datos se procesan en el Servicio por cuenta de la Agencia.',
          '“Cuenta” o “Licencia” – perfil individual asociado a la suscripción de la Agencia.',
          '“Datos del Servicio” – datos, archivos, formularios y contenidos cargados o generados dentro del Servicio.',
          '“Datos Personales” – cualquier información sobre una persona física identificada o identificable según el RGPD.',
          '“Información Confidencial” – información no pública revelada por cualquiera de las partes en relación con el Servicio.'
        ]
      },
      {
        id: 'admisibilidad',
        title: '3. Admisibilidad y Registro',
        paragraphs: [
          'Los Usuarios deben tener como mínimo dieciséis (16) años y capacidad legal para contratar según la normativa aplicable.',
          'Las Agencias son responsables de facilitar datos veraces durante el registro y de gestionar los permisos de usuario de acuerdo con su estructura y obligaciones legales.',
          'LiveLoveSpain podrá suspender o cancelar cuentas si se facilita información inexacta, no se puede verificar la identidad o se incumplen estas Condiciones.'
        ]
      },
      {
        id: 'acceso',
        title: '4. Acceso y Uso del Servicio',
        paragraphs: [
          'LiveLoveSpain concede a la Agencia una licencia no exclusiva, intransferible y revocable para acceder y utilizar el Servicio conforme a estas Condiciones y a la ley.',
          'Se prohíbe al Usuario: (a) copiar o modificar el Servicio; (b) llevar a cabo ingeniería inversa; (c) interferir en la seguridad o rendimiento; (d) eludir controles de acceso; (e) utilizar el Servicio con fines ilícitos, dañinos o fraudulentos.',
          'LiveLoveSpain persigue una disponibilidad mensual del 99,5 %. No obstante, podrá realizar tareas de mantenimiento, actualizaciones o interrupciones de emergencia. Cuando sea posible, se notificará con antelación.'
        ]
      },
      {
        id: 'facturacion',
        title: '5. Suscripciones, Facturación y Pago',
        paragraphs: [
          'Las suscripciones se facturan por adelantado de forma mensual o anual a través de nuestro procesador de pagos, Stripe Payments Europe Limited. Los importes incluyen el IVA aplicable salvo indicación contraria.',
          'Las suscripciones se renuevan automáticamente al final de cada periodo de facturación salvo cancelación conforme a la Sección 6. La Agencia puede consultar facturas y recibos desde el propio Servicio.',
          'Usted autoriza a LiveLoveSpain a cargar en el método de pago registrado las cuotas, cargos adicionales y tributos aplicables. La falta de pago puede implicar la suspensión del Servicio.',
          'LiveLoveSpain podrá revisar los precios avisando con al menos treinta (30) días de antelación. El uso continuado tras la fecha efectiva implica la aceptación de los nuevos precios.'
        ]
      },
      {
        id: 'prueba',
        title: '6. Periodo de Prueba, Degradaciones y Cancelación',
        paragraphs: [
          'Las nuevas Agencias disponen de un período de prueba gratuito de catorce (14) días. Si no se cancela antes de su finalización, el plan seleccionado se convertirá en suscripción de pago aplicando los días de prueba restantes según proceda.',
          'La Agencia puede cancelar en cualquier momento desde el portal de facturación. La cancelación surte efecto al término del periodo en curso; no se realizan reembolsos parciales salvo disposición legal.',
          'Tras la terminación, el acceso al Servicio finalizará. Recomendamos exportar los datos antes de la fecha efectiva. LiveLoveSpain conservará los Datos del Servicio durante un máximo de treinta (30) días para posibles recuperaciones, transcurridos los cuales se eliminarán salvo obligación legal.'
        ]
      },
      {
        id: 'propiedad',
        title: '7. Propiedad Intelectual y Contenidos',
        paragraphs: [
          'LiveLoveSpain mantiene todos los derechos de propiedad intelectual sobre el Servicio, incluidos software, interfaces, documentación y marcas.',
          'La Agencia conserva la titularidad de los Datos del Servicio que cargue o genere. Al utilizar el Servicio, concede a LiveLoveSpain una licencia limitada para alojar, procesar, transmitir y mostrar dichos datos con la única finalidad de prestar y mejorar el Servicio.',
          'La Agencia garantiza que los Datos del Servicio cumplen la normativa y no infringen derechos de terceros.'
        ]
      },
      {
        id: 'proteccion-datos',
        title: '8. Protección de Datos y Cumplimiento RGPD',
        paragraphs: [
          'LiveLoveSpain actúa como Encargado del Tratamiento respecto a los datos personales gestionados por las Agencias dentro del Servicio. Las Agencias actúan como Responsables. LiveLoveSpain actúa como Responsable respecto a la información de la cuenta y facturación de la Agencia.',
          'El tratamiento se realiza conforme al Reglamento (UE) 2016/679 (RGPD), a la Ley Orgánica 3/2018 (LOPDGDD) y a la Ley 34/2002 (LSSI-CE).',
          'El Acuerdo de Encargado de Tratamiento (DPA) regula las obligaciones, subencargados, medidas de seguridad y asistencia en el ejercicio de derechos. El DPA forma parte integrante del contrato y está disponible en [livelovespain.com/crm/dpa](/crm/dpa).',
          'LiveLoveSpain notificará a la Agencia sin demora indebida y, en cualquier caso, dentro de las setenta y dos (72) horas siguientes a tener conocimiento de una violación de seguridad que afecte a los Datos del Servicio.'
        ]
      },
      {
        id: 'seguridad',
        title: '9. Seguridad y Confidencialidad',
        paragraphs: [
          'LiveLoveSpain aplica medidas técnicas y organizativas apropiadas, incluyendo cifrado en tránsito y en reposo, control de accesos basado en roles, registros de actividad, copias de seguridad periódicas y gestión de vulnerabilidades.',
          'Cada parte se compromete a tratar la Información Confidencial con el mismo grado de diligencia que emplea para proteger la suya y a utilizarla únicamente para cumplir con el contrato.',
          'La Agencia deberá garantizar la confidencialidad de las credenciales y notificar de inmediato cualquier acceso no autorizado o sospecha de brecha de seguridad.'
        ]
      },
      {
        id: 'terceros',
        title: '10. Servicios e Integraciones de Terceros',
        paragraphs: [
          'El Servicio puede apoyarse en proveedores externos (por ejemplo, alojamiento, pagos, correo electrónico, funcionalidades de IA). LiveLoveSpain selecciona dichos subencargados con la debida diligencia y mantiene acuerdos de tratamiento con cada uno.',
          'El uso de integraciones externas opcionales habilitadas por la Agencia se realiza bajo su exclusiva responsabilidad y queda sujeto a las condiciones del tercero correspondiente.'
        ]
      },
      {
        id: 'responsabilidad',
        title: '11. Limitación de Responsabilidad',
        paragraphs: [
          'En la máxima medida permitida por la ley, LiveLoveSpain no será responsable de daños indirectos, incidentales, especiales, consecuenciales, ejemplares o punitivos, ni de pérdida de beneficios, ingresos, datos o oportunidades de negocio.',
          'La responsabilidad total de LiveLoveSpain derivada de estas Condiciones se limita al importe pagado por la Agencia en los doce (12) meses anteriores al evento que origine la reclamación.',
          'Nada de lo dispuesto limita la responsabilidad por muerte o daños personales causados por negligencia, dolo o cualquier otra responsabilidad que no pueda excluirse según la ley aplicable.'
        ]
      },
      {
        id: 'terminacion',
        title: '12. Suspensión y Resolución',
        paragraphs: [
          'LiveLoveSpain podrá suspender o terminar el acceso si: (a) existen facturas impagadas; (b) se incumplen estas Condiciones o la ley; (c) existe un riesgo para la seguridad; o (d) se recibe un requerimiento legal.',
          'En caso de resolución por causa imputable a la Agencia, LiveLoveSpain podrá deshabilitar el acceso de forma inmediata. La Agencia seguirá siendo responsable de las cantidades devengadas hasta la fecha efectiva.',
          'La Agencia podrá solicitar una copia de los Datos del Servicio durante el periodo de conservación previsto en la Sección 6. LiveLoveSpain no está obligada a conservarlos más allá de los plazos legales.'
        ]
      },
      {
        id: 'ley-aplicable',
        title: '13. Ley Aplicable y Jurisdicción',
        paragraphs: [
          'Estas Condiciones se rigen por la legislación española y la normativa de la Unión Europea aplicable, sin atender a conflictos de leyes.',
          'Las partes se someten a los Juzgados y Tribunales de Madrid (España). Los consumidores de la UE pueden acudir asimismo a la plataforma de Resolución de Litigios en Línea de la Comisión Europea (http://ec.europa.eu/odr).'
        ]
      },
      {
        id: 'modificaciones',
        title: '14. Modificaciones',
        paragraphs: [
          'LiveLoveSpain podrá modificar estas Condiciones por motivos legales, operativos o de seguridad. Los cambios sustanciales se comunicarán con al menos treinta (30) días de antelación por correo electrónico o aviso en la aplicación.',
          'El uso continuado tras la fecha efectiva constituye aceptación de las Condiciones modificadas. Versiones previas quedarán archivadas con fines de consulta.'
        ]
      },
      {
        id: 'contacto',
        title: '15. Contacto',
        paragraphs: [
          'Para cualquier consulta relativa a estas Condiciones puede contactar con:',
          'LiveLoveSpain',
          'Correo: info@livelovespain.com'
        ]
      }
    ],
    spanishLinkLabel: 'See the English version'
  }
};

const Terms = () => {
  const { currentLanguage } = useLanguage();
  const languageKey = currentLanguage === 'es' ? 'es' : 'en';
  const content = termsContent[languageKey];
  const alternateLanguageKey = languageKey === 'es' ? 'en' : 'es';
  // Simple paths - basename handles /crm prefix automatically
  // Language-prefixed routes are handled by /:lang/crm/* route in App.js
  // For now, just use simple path - language switching on public pages doesn't use URL prefixes
  const alternateLink = languageKey === 'es' ? '/terms?lang=en' : '/terms?lang=es';

  // Get canonical URL based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
  const canonicalUrl = isSubdomain 
    ? 'https://crm.livelovespain.com/terms'
    : 'https://www.livelovespain.com/crm/terms';

  // Add JSON-LD schemas
  React.useEffect(() => {
    const baseUrl = isSubdomain ? 'https://crm.livelovespain.com' : 'https://www.livelovespain.com/crm';
    
    // Breadcrumbs Schema
    const breadcrumbsId = 'jsonld-breadcrumbs';
    let breadcrumbsScript = document.getElementById(breadcrumbsId);
    const breadcrumbsPayload = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl + "/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Terms of Service",
          "item": canonicalUrl
        }
      ]
    };
    if (!breadcrumbsScript) {
      breadcrumbsScript = document.createElement('script');
      breadcrumbsScript.type = 'application/ld+json';
      breadcrumbsScript.id = breadcrumbsId;
      document.head.appendChild(breadcrumbsScript);
    }
    breadcrumbsScript.text = JSON.stringify(breadcrumbsPayload);

    // WebPage Schema
    const webpageId = 'jsonld-webpage';
    let webpageScript = document.getElementById(webpageId);
    const webpagePayload = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms of Service - LiveLoveSpain CRM",
      "description": "Terms of Service for LiveLoveSpain CRM. Read our terms and conditions for using our relocation and immigration management platform.",
      "url": canonicalUrl,
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "name": "LiveLoveSpain CRM",
        "url": baseUrl
      },
      "about": {
        "@type": "Thing",
        "name": "Terms of Service"
      },
      "publisher": {
        "@type": "Organization",
        "name": "LiveLoveSpain"
      }
    };
    if (!webpageScript) {
      webpageScript = document.createElement('script');
      webpageScript.type = 'application/ld+json';
      webpageScript.id = webpageId;
      document.head.appendChild(webpageScript);
    }
    webpageScript.text = JSON.stringify(webpagePayload);

    return () => {
      // Cleanup on unmount
      const breadcrumbs = document.getElementById(breadcrumbsId);
      const webpage = document.getElementById(webpageId);
      if (breadcrumbs) breadcrumbs.remove();
      if (webpage) webpage.remove();
    };
  }, [canonicalUrl, isSubdomain]);

  return (
    <>
      <Helmet>
        <title>Terms of Service - LiveLoveSpain CRM</title>
        <meta name="description" content="Terms of Service for LiveLoveSpain CRM. Read our terms and conditions for using our relocation and immigration management platform." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Terms of Service - LiveLoveSpain CRM" />
        <meta property="og:description" content="Terms of Service for LiveLoveSpain CRM. Read our terms and conditions for using our relocation and immigration management platform." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Terms of Service - LiveLoveSpain CRM" />
        <meta name="twitter:description" content="Terms of Service for LiveLoveSpain CRM. Read our terms and conditions for using our relocation and immigration management platform." />
      </Helmet>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          {content.backLabel}
        </Link>

        <div className="bg-card rounded-xl shadow-lg p-8 md:p-12 space-y-6">
          <header className="space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              {content.title}
            </h1>
            <p className="text-muted-foreground text-sm">
              Last updated / Última actualización: {TERMS_LAST_UPDATED}
            </p>
            <p className="text-sm text-muted-foreground">
              {content.languageNote}{' '}
              <Link to={alternateLink} className="text-blue-600 hover:text-blue-500">
                {termsContent[alternateLanguageKey].spanishLinkLabel}
              </Link>
              .
            </p>
          </header>

          <nav className="bg-muted/40 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
              {content.tableOfContentsTitle}
            </h2>
            <ul className="grid gap-2 md:grid-cols-2 text-sm">
              {content.sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-blue-600 hover:text-blue-500"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            {content.sections.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-24 mb-10">
                <h2 className="text-2xl font-semibold text-foreground">{section.title}</h2>
                {section.paragraphs?.map((paragraph, index) => {
                  // Handle links in paragraphs (format: [text](url))
                  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
                  const parts = [];
                  let lastIndex = 0;
                  let match;
                  
                  while ((match = linkRegex.exec(paragraph)) !== null) {
                    // Add text before the link
                    if (match.index > lastIndex) {
                      parts.push({ type: 'text', content: paragraph.substring(lastIndex, match.index) });
                    }
                    // Add the link
                    parts.push({ type: 'link', text: match[1], url: match[2] });
                    lastIndex = match.index + match[0].length;
                  }
                  
                  // Add remaining text
                  if (lastIndex < paragraph.length) {
                    parts.push({ type: 'text', content: paragraph.substring(lastIndex) });
                  }
                  
                  // If no links found, just return the paragraph as-is
                  if (parts.length === 0) {
                    parts.push({ type: 'text', content: paragraph });
                  }
                  
                  return (
                    <p key={index} className="text-muted-foreground leading-relaxed">
                      {parts.map((part, i) => {
                        if (part.type === 'link') {
                          const isInternal = part.url.startsWith('/');
                          return isInternal ? (
                            <Link key={i} to={part.url} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline">
                              {part.text}
                            </Link>
                          ) : (
                            <a key={i} href={part.url} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">
                              {part.text}
                            </a>
                          );
                        }
                        return <span key={i}>{part.content}</span>;
                      })}
                    </p>
                  );
                })}
                {section.list && (
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    {section.list.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Terms;

